import { useAppSelector } from "../../../redux/hooks";
import { lddGlobalSessionInfo, ProvincesShortCode } from "../../navigationTypes";
import KeyDate from "../../taskSystem/KeyDate";
import { ID_FIRMCONTACT, LABEL_LAWYERONFILE, ID_LAWYERONFILE, TaskPreferenceItem, TEXTID_LAWYERONFILE, TEXTID_FIRMCONTACT, LABEL_FIRMCONTACT } from "../../../../../libs/taskmanagement/src/taskTypes";
import { mapKeydateToID } from '../../../../../libs/taskmanagement/src/TaskModule';
import axios from "axios";
import { convertXMLItemsToJSON } from "../../../lib/xmlFuncs";
import { TKeyDate } from "../../taskSystem/KeyDates";

export function parseProfile(tasks: TaskPreferenceItem[]){
    return tasks.map(item => {
        if(!item.offset || item.offset === "") {
            item.offset = '0';
        }
        if(!item.output || item.output === "") {
            item.output = '0';
        }
        if(!item.applyToFileType) {
            item.applyToFileType = 'PSM';
        }
        if(!item.isDirty) {
            item.isDirty = '0';
        }
        if(!item.namesAssignedTo) {
            item.namesAssignedTo = '';
        }
        if(!item.idsAssignedTo) {
            item.idsAssignedTo = `${ID_FIRMCONTACT}-${ID_FIRMCONTACT}-${TEXTID_FIRMCONTACT}`;
        }
        if(item.CalendarItemDate){
            let value = item.CalendarItemDate || '';
            value = value.replace(/([A-Z])/g, (match, p1, offset) =>
                offset === 0 ? p1 : ` ${p1}`
            );
            value = value.replace(/ Date$/, '');
            value = value.trim().replace(/\s+/g, ' ');
            item.CalendarItemDate = value;
        }
        const assignedUsers = item.idsAssignedTo.split(',');
        const hasFirmContact = assignedUsers.some(entry => {
            const parts = entry.split('-');
            return parts.length > 1 && parts[1] === `${ID_FIRMCONTACT}`;
        });
        if (!hasFirmContact && assignedUsers.length === 0) {
            assignedUsers.unshift(`${ID_FIRMCONTACT}-${ID_FIRMCONTACT}-${TEXTID_FIRMCONTACT}`);
            item.idsAssignedTo = assignedUsers.join(',');
        }
        return item;
    })
}

export function updateStandardItem(standardItem: TaskPreferenceItem, defaultItem: TaskPreferenceItem): TaskPreferenceItem {
    const keys = [...new Set([...Object.keys(standardItem), ...Object.keys(defaultItem)])];
    let isDifferent = false;
    
    for (const key of keys) {
        if (['isDirty', 'output', 'idsAssignedTo', 'description'].includes(key))
            continue;
        
        if (standardItem[key] && defaultItem[key] && (standardItem[key] !== defaultItem[key])) {
            isDifferent = true;
            break;
        }
    }

    standardItem.description = defaultItem.description;

    if (isDifferent && standardItem.isDirty === undefined) {
        standardItem.isDirty = '1';
    }
    if(standardItem.CalendarItemDate && standardItem.CalendarItemDate !== defaultItem.CalendarItemDate && (!standardItem.isDirty || standardItem.isDirty === '0')){
        standardItem.CalendarItemDate = defaultItem.CalendarItemDate;
    }
    
    return standardItem;
}

export class TaskFuncs {

    private SESSION_INFO: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);
    private RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${this.SESSION_INFO?.appProvinceShort}/${this.SESSION_INFO?.firm?.id}`;

    getCalendarProfile(): Promise<Response>[] {
        const response = fetch(`getprofile.asp?pname=CalendarProfile&lfdata=1&SessionID=${this.SESSION_INFO?.sid}`);
        return [response]
    }

    async getUsers(userID: string, sessionID: string, newFileRequest = false) {
        try {
            const response = await axios.get(
                `/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${userID}/uv/get/users?SessionID=${sessionID}`
            );
            
            const formattedNewUsers = response.data.result.map((user: any) => ({
                UserID: user.LDDUserID,
                Name: user.Name,
                LDDUserName: user.SubUsername,
                SubUserId: user.SubUserID
            }));
            
            const defaultUsers = [
                {
                    UserID: ID_LAWYERONFILE,
                    Name: LABEL_LAWYERONFILE,
                    LDDUserName: TEXTID_LAWYERONFILE,
                    SubUserId: ID_LAWYERONFILE
                },
                {
                    UserID: ID_FIRMCONTACT,
                    Name: LABEL_FIRMCONTACT,
                    LDDUserName: TEXTID_FIRMCONTACT,
                    SubUserId: ID_FIRMCONTACT
                }
            ];
            if(newFileRequest){
                return formattedNewUsers;
            }
            return [...defaultUsers, ...formattedNewUsers];
        } catch (error) {
            console.error("Error fetching users:", error);
            throw error;
        }
    }

    getProvinceTasks(province: ProvincesShortCode): TKeyDate[] {
        try {
            const result: TKeyDate[] = [];
            KeyDate.setProv(province);
            const keydates = KeyDate.KeyDatesDefine.dates;
            for (const keydate of keydates) {
                const isValidProvince = !keydate.province || keydate.province.includes(province);
                if(isValidProvince){
                    if(!keydate.title && keydate.titleFunc){
                        keydate.title = keydate.titleFunc();
                    }
                    if(keydate.id === 1 && province === 'MB'){
                        keydate.title = "Closing";
                    }
                    result.push(keydate);
                }
            }
            return result;
        } catch (error) {
            console.error('Error in getting province tasks:', error);
            throw error;
        }
    }

    handleDefaultData(data: string) {
        let ShowByMatterNum = "1";
        let ShowClientName = "1";
        let ShowTransactionType = "1";
        let ShowClerk = "1";
        let ShowFileNumber = "1";
        let ShowLawyer = "1";
        let ShowTransactionTypeShort = "1";
        let standardTasks: TaskPreferenceItem[] = null;
        
        if (data) {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(data, "text/xml");
            if (xmlDoc) {
                const items = xmlDoc.getElementsByTagName('items')[0];
                if (items) {
                    if (items.getAttribute('ShowByMatterNum') && items.getAttribute('ShowByMatterNum') > "") {
                        ShowByMatterNum = items.getAttribute('ShowByMatterNum');
                    }
                    if (items.getAttribute('ShowClientName') && items.getAttribute('ShowClientName') > "") {
                        ShowClientName = items.getAttribute('ShowClientName');
                    }
                    if (items.getAttribute('ShowTransactionType') && items.getAttribute('ShowTransactionType') > "") {
                        ShowTransactionType = items.getAttribute('ShowTransactionType');
                    }
                    if (items.getAttribute('ShowTransactionTypeShort') && items.getAttribute('ShowTransactionTypeShort') > "") {
                        ShowTransactionTypeShort = items.getAttribute('ShowTransactionTypeShort');
                    }
                    if (items.getAttribute('ShowClerk') && items.getAttribute('ShowClerk') > "") {
                        ShowClerk = items.getAttribute('ShowClerk');
                    }
                    if (items.getAttribute('ShowFileNumber') && items.getAttribute('ShowFileNumber') > "") {
                        ShowFileNumber = items.getAttribute('ShowFileNumber');
                    }
                    if (items.getAttribute('ShowLawyer') && items.getAttribute('ShowLawyer') > "") {
                        ShowLawyer = items.getAttribute('ShowLawyer');
                    }
                    
                    let defaultTaskItems = convertXMLItemsToJSON(items.getElementsByTagName('standardCalendarItems')[1]);
                    standardTasks = parseProfile(defaultTaskItems);
                }
            }
        }
        
        return {
            taskSettings: {
                ShowByMatterNum,
                ShowClientName,
                ShowTransactionType,
                ShowTransactionTypeShort,
                ShowClerk,
                ShowFileNumber,
                ShowLawyer
            },
            standardTasks
        };
    }

    getHighlightedText(text: string, highlight: string) {
        const highlightText = highlight;
        
        highlight = highlight.replace(/\\/g, "");
        highlight = highlight.replace(/\*/g, "\\*");
        highlight = highlight.replace(/\+/g, "\\+");
        highlight = highlight.replace(/\./g, "\\.");
        highlight = highlight.replace(/\?/g, "\\?");
        highlight = highlight.replace(/\[/g, "\\[");
        highlight = highlight.replace(/\]/g, "\\]");
        highlight = highlight.replace(/\{/g, "\\{");
        highlight = highlight.replace(/\}/g, "\\}");
        highlight = highlight.replace(/\(/g, "\\(");
        highlight = highlight.replace(/\)/g, "\\)");
        
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
        
        return { highlightText, parts };
    }

    convertTasksToXML(allTasks: any[], taskSettings: any): string {
        let xmlString = '<items';
        if (taskSettings) {
            for (const key in taskSettings) {
                if (taskSettings.hasOwnProperty(key)) {
                    xmlString += ` ${key}="${taskSettings[key]}"`;
                }
            }
        }
        
        xmlString += '>';
        
        let standardCalendarItems = '';
        let userCalendarItems = '';
        let largestUserTypeID = 1001;
        
        allTasks.forEach(task => {
            let calendarItem = '<calendarItem';
            Object.keys(task).forEach(key => {
                if (task[key] !== null && task[key] !== undefined) {
                    const escapedValue = String(task[key])
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&apos;');
                        
                    calendarItem += ` ${key}="${escapedValue}"`;
                }
                else {
                    calendarItem += ` ${key}=""`;
                }
            });
            
            calendarItem += ' />';
            const typeID = parseInt(task.typeID || '0', 10);
            
            if (typeID >= 0 && typeID <= 150) {
                standardCalendarItems += calendarItem;
            } else {
                userCalendarItems += calendarItem;
                if (typeID > largestUserTypeID) {
                    largestUserTypeID = typeID;
                }
            }
        });
        
        xmlString += '<standardCalendarItems>';
        xmlString += standardCalendarItems;
        xmlString += '</standardCalendarItems>';
        xmlString += `<userCalendarItems>`;
        xmlString += userCalendarItems;
        xmlString += '</userCalendarItems>';
        xmlString += '</items>';
        
        return xmlString;
    }

    async settings() {
        const response = await fetch(`${this.RWAPI}/tasksettings?SessionID=${this.SESSION_INFO?.sid}`);
        const data = await response.json();
        return data.result;
    }

    async edit(updatedTask: TaskPreferenceItem, originalTask: TaskPreferenceItem, allTasks: TaskPreferenceItem[], taskSettings: any, isReset: boolean, provinceTasks: any[], taskbadgeLabel?: string) : Promise<any> {
        try{
            const taskChanges = Object.keys(updatedTask).some(key => 
                key !== 'output' && 
                JSON.stringify(updatedTask[key]) !== JSON.stringify(originalTask[key])
            );

            updatedTask.isDirty = (isReset || (!taskChanges && taskbadgeLabel && taskbadgeLabel !== "Custom")) ? "0" : "1";
            updatedTask.namesAssignedTo= '';
            allTasks = mapKeydateToID(provinceTasks, allTasks);
            updatedTask = mapKeydateToID(provinceTasks, [updatedTask])[0];
            const editTaskIndex = allTasks.findIndex(task => task.typeID === originalTask.typeID);
            if (editTaskIndex !== -1) {
                allTasks = [
                    ...allTasks.slice(0, editTaskIndex),
                    updatedTask, 
                    ...allTasks.slice(editTaskIndex + 1)
                ];
            }
            const taskItemsXML = this.convertTasksToXML(allTasks, taskSettings);
            const response = await fetch(`saveprofile.asp?pname=CalendarProfile&SessionID=${this.SESSION_INFO?.sid}`, {
                method: "POST",
                body: taskItemsXML
            });
            const responseText = await response.text();
            const isSuccessful = responseText.toLowerCase().includes("saved successfully");
            if(isSuccessful){
                await KeyDate.MassUpdateTasks(this.SESSION_INFO, updatedTask, provinceTasks, taskSettings);
            }

            return {
                ok: isSuccessful,
                result: allTasks
            };

        } catch (error) {
            console.log("[Task Management]: Failed to edit task details.")
        }
    }

    async add(newTask: TaskPreferenceItem, allTasks: TaskPreferenceItem[], taskSettings: any, provinceTasks: any[]): Promise<any> {
        try{
            newTask.namesAssignedTo= '';
            const propertyMappings = {
                shortDescr: "@MatterNumber",
                fileType: "@PSM",
                tableLinkedToID: "TPAppXML",
                linkedID: "AppID",
                recordID: "AppID",
                useLawyerID: "1",
                useStationID: "0",
                isDirty: "1"
            };
              
            Object.entries(propertyMappings).forEach(([property, value]) => {
                if (newTask[property] || newTask[property] === '') {
                    newTask[property] = value;
                }
            });

            let maxTypeID = 0;
            allTasks.forEach(task => {
            if (task.typeID) {
                const typeIDNum = parseInt(task.typeID, 10);
                if (!isNaN(typeIDNum) && typeIDNum > maxTypeID) {
                    maxTypeID = typeIDNum;
                }
            }
            });
            maxTypeID = Math.max(maxTypeID, 1001);
            newTask.typeID = (maxTypeID + 1).toString();
            allTasks = mapKeydateToID(provinceTasks, allTasks);
            newTask = mapKeydateToID(provinceTasks, [newTask])[0];
            allTasks.push(newTask);
            const taskItemsXML = this.convertTasksToXML(allTasks, taskSettings);
            const response = await fetch(`saveprofile.asp?pname=CalendarProfile&SessionID=${this.SESSION_INFO?.sid}`, {
                method: "POST",
                body: taskItemsXML
            });
            const responseText = await response.text();
            const isSuccessful = responseText.toLowerCase().includes("saved successfully");
            if(isSuccessful){
                await KeyDate.MassUpdateTasks(this.SESSION_INFO, newTask, provinceTasks, taskSettings);
            }

            return {
                ok: isSuccessful,
                result: allTasks
            };


        } catch (error) {
            console.log("[Task Management]: Failed to add task details.")
        }
    }

    async saveSettings(taskSettings: Record<string, string>, allTasks: TaskPreferenceItem[]): Promise<any> {
        try{
            const taskItemsXML = this.convertTasksToXML(allTasks, taskSettings);
            const response = await fetch(`saveprofile.asp?pname=CalendarProfile&SessionID=${this.SESSION_INFO?.sid}`, {
                method: "POST",
                body: `<Root>${taskItemsXML}</Root>`
            });
            const responseText = await response.text();
            const isSuccessful = responseText.toLowerCase().includes("saved successfully");

            return {
                ok: isSuccessful,
                result: allTasks
            };

        } catch (error) {
            console.log("[Task Management]: Failed to save task settings.")
        }
    }

}